/**
 * Opus 4.8 accepts images up to 2576px on the longest edge and downscales
 * anything larger, so pixels beyond this are bytes the model never reads.
 */
export const DEFAULT_MAX_IMAGE_DIMENSION = 2576;
export const DEFAULT_IMAGE_QUALITY = 0.9;

/** WebP keeps alpha and beats JPEG on size at the same quality. */
const WEBP_CONTENT_TYPE = "image/webp";
/** For browsers that cannot encode WebP from a canvas. */
const JPEG_CONTENT_TYPE = "image/jpeg";

type CompressImageOptions = {
  maxDimension?: number;
  quality?: number;
};

const scaleToFit = (
  bitmap: ImageBitmap,
  maxDimension: number,
): { width: number; height: number } => {
  const scale = Math.min(
    1,
    maxDimension / Math.max(bitmap.width, bitmap.height),
  );
  return {
    width: Math.max(1, Math.round(bitmap.width * scale)),
    height: Math.max(1, Math.round(bitmap.height * scale)),
  };
};

/**
 * The encoding {@link compressImageBlob} targets: WebP wherever it can be
 * encoded, JPEG otherwise.
 *
 * `canvas.toBlob` silently falls back to PNG for a type it cannot encode, and a
 * PNG of a photo is larger than the JPEG it replaced — so pick the encoding up
 * front rather than discovering it from the output blob.
 */
export function compressedImageContentType(): string {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  return canvas
    .toDataURL(WEBP_CONTENT_TYPE)
    .startsWith(`data:${WEBP_CONTENT_TYPE}`)
    ? WEBP_CONTENT_TYPE
    : JPEG_CONTENT_TYPE;
}

/**
 * Downscale an image to fit within `maxDimension` on its longest edge and
 * re-encode it, to keep it small enough to inline into a request body.
 *
 * Read the returned blob's `type` for the encoding that was chosen — callers
 * that persist a filename or MIME must follow it rather than assume WebP.
 *
 * Returns the original blob when the browser gives us no 2D canvas context.
 * Throws if the blob cannot be decoded as an image.
 */
export async function compressImageBlob(
  blob: Blob,
  { maxDimension, quality }: CompressImageOptions = {},
): Promise<Blob> {
  // Resolve the encoding before building the canvas we draw into: detection
  // needs a canvas of its own, and doing it inline would interleave the two.
  const contentType = compressedImageContentType();

  const bitmap = await createImageBitmap(blob);
  try {
    const { width, height } = scaleToFit(
      bitmap,
      maxDimension ?? DEFAULT_MAX_IMAGE_DIMENSION,
    );
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return blob;

    if (contentType === JPEG_CONTENT_TYPE) {
      // JPEG has no alpha; fill white so transparent areas don't render black.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(bitmap, 0, 0, width, height);

    return await new Promise<Blob>((resolve) => {
      canvas.toBlob(
        (out) => resolve(out ?? blob),
        contentType,
        quality ?? DEFAULT_IMAGE_QUALITY,
      );
    });
  } finally {
    bitmap.close();
  }
}
